/**
 * The opportunity list's columns.
 *
 * Declared as data rather than as JSX inside the table, because the column
 * chooser persists a set of ids in `localStorage` and the CSV of the current
 * view has to match what is on screen. One list, three readers.
 *
 * Two of these carry a spec rule the renderer cannot be trusted to remember:
 *
 *  - **every value cell states its basis** (§6.11) — net and customer are
 *    different numbers and the reader must always know which one they are
 *    looking at;
 *  - **BU is a set** (§5.1 [R2]) — 18% of real opportunities span two or three
 *    business units, so the column is "BU mix" and renders chips, never a
 *    single value.
 */

import { Link } from 'react-router-dom';
import { RiskBadges } from '@/components/domain/RiskBadges';
import { BusinessUnitChips, StatusBadge } from '@/components/domain/Chips';
import { MoneyCell, PriceBasisTag } from '@/components/domain/Money';
import { StageProgress } from '@/components/domain/StageStepper';
import { formatDate, formatMoney, humanize } from '@/lib/format';
import { FOCUS_HELP_TEXT, type OpportunitySortKey } from '@menlo/shared';

export type Row = Record<string, unknown>;

export interface ColumnDef {
  id: string;
  label: string;
  /**
   * The `sort` value the API accepts. Every column has one (docs/bugs-fixed.md, 2026-09-27:
   * "every column in the list is sortable"), and it must come from
   * `OPPORTUNITY_SORT_KEYS` — a key the API has no SQL for would render a header
   * button that quietly does nothing. `column-sort.test.tsx` walks the list.
   */
  sortKey: OpportunitySortKey;
  align?: 'left' | 'right';
  /**
   * The column's default width, in pixels, and nothing more — a user's own
   * width overrides it.
   *
   * These were Tailwind class strings (`'w-28'`, `'w-72'`) that the table
   * pasted onto the `th`. A dragged width cannot be a class name, so they are
   * numbers now, at the same pixel values the classes resolved to (`w-28` is
   * `7rem`, 112px). Nothing else consumed the class.
   */
  width: number;
  render: (row: Row) => React.ReactNode;
  /** Plain text, for the CSV of the current view. */
  text: (row: Row) => string;
}

/**
 * The bounds a width is held inside, wherever it comes from — a drag, a
 * double-click fit, or a value read back out of `localStorage`.
 *
 * The minimum is a column that can still be grabbed again; the maximum is what
 * stops one 400-character `next_step` from making fit-to-contents produce a
 * column nobody can scroll past.
 */
export const MIN_COLUMN_WIDTH = 64;
export const MAX_COLUMN_WIDTH = 640;

export function clampColumnWidth(width: number): number {
  return Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, Math.round(width)));
}

const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

export const COLUMNS: ColumnDef[] = [
  {
    id: 'opp_number',
    label: 'Number',
    sortKey: 'opp_number',
    width: 112,
    render: (row) => (
      <Link to={`/opportunities/${str(row['id'])}`} className="font-mono text-xs hover:underline">
        {str(row['opp_number'])}
      </Link>
    ),
    text: (row) => str(row['opp_number']),
  },
  {
    id: 'name',
    label: 'Name',
    sortKey: 'name',
    width: 288,
    render: (row) => (
      <Link to={`/opportunities/${str(row['id'])}`} className="font-medium hover:underline">
        {str(row['name'])}
      </Link>
    ),
    text: (row) => str(row['name']),
  },
  {
    id: 'account_name',
    label: 'Account',
    sortKey: 'account',
    width: 224,
    render: (row) => (
      <Link to={`/accounts/${str(row['account_id'])}`} className="hover:underline">
        {str(row['account_name'])}
      </Link>
    ),
    text: (row) => str(row['account_name']),
  },
  {
    id: 'stage',
    label: 'Stage',
    sortKey: 'stage',
    width: 192,
    render: (row) => (
      <StageProgress
        stageCode={str(row['stage_code'])}
        stageName={row['stage_name'] as string | undefined}
        sortOrder={row['stage_sort_order'] as number | undefined}
      />
    ),
    text: (row) => str(row['stage_name'] ?? row['stage_code']),
  },
  {
    id: 'status',
    label: 'Status',
    sortKey: 'status',
    width: 112,
    render: (row) => <StatusBadge status={str(row['status'])} />,
    text: (row) => str(row['status']),
  },
  {
    id: 'owner_name',
    label: 'Owner',
    sortKey: 'owner',
    width: 144,
    render: (row) => str(row['owner_name']) || '—',
    text: (row) => str(row['owner_name']),
  },
  {
    id: 'fae_name',
    label: 'FAE',
    sortKey: 'fae',
    width: 144,
    render: (row) => str(row['fae_name']) || '—',
    text: (row) => str(row['fae_name']),
  },
  {
    id: 'annual_revenue_net',
    label: 'Annual revenue (net)',
    sortKey: 'annual_revenue_net',
    align: 'right',
    width: 176,
    render: (row) => (
      <span className="flex items-center justify-end gap-1.5">
        <MoneyCell value={row['annual_revenue_net'] as string} />
        <PriceBasisTag basis={row['price_basis'] as string} />
      </span>
    ),
    text: (row) => formatMoney(row['annual_revenue_net'] as string),
  },
  {
    id: 'annual_revenue_customer',
    label: 'Annual revenue (customer)',
    sortKey: 'annual_revenue_customer',
    align: 'right',
    width: 192,
    render: (row) => <MoneyCell value={row['annual_revenue_customer'] as string} />,
    text: (row) => formatMoney(row['annual_revenue_customer'] as string),
  },
  {
    id: 'lifetime_value',
    label: 'Lifetime value',
    sortKey: 'lifetime_value',
    align: 'right',
    width: 144,
    render: (row) => <MoneyCell value={row['lifetime_value'] as string} />,
    text: (row) => formatMoney(row['lifetime_value'] as string),
  },
  {
    id: 'weighted_revenue',
    label: 'Weighted',
    sortKey: 'weighted_revenue',
    align: 'right',
    width: 128,
    render: (row) => <MoneyCell value={row['weighted_revenue'] as string} />,
    text: (row) => formatMoney(row['weighted_revenue'] as string),
  },
  {
    id: 'effective_probability',
    label: 'Prob.',
    sortKey: 'effective_probability',
    align: 'right',
    width: 80,
    render: (row) => `${str(row['effective_probability'])}%`,
    text: (row) => `${str(row['effective_probability'])}%`,
  },
  {
    // Not in the spec's default list (04-PHASE-ui.md), which simply never named
    // it; the detail page has always shown it under "Dates". Placed before the
    // ramp date so the dates on screen read in the order a deal lives them.
    id: 'created_date',
    label: 'Created',
    sortKey: 'created_date',
    width: 128,
    render: (row) => formatDate(row['created_date'] as string),
    text: (row) => str(row['created_date']),
  },
  {
    id: 'expected_ramp_date',
    label: 'Ramp date',
    sortKey: 'expected_ramp_date',
    width: 144,
    render: (row) =>
      row['actual_ramp_date'] ? (
        <span title="Actual ramp date — production has started.">
          {formatDate(row['actual_ramp_date'] as string)}{' '}
          <span className="text-[10px] uppercase text-muted-foreground">actual</span>
        </span>
      ) : (
        formatDate(row['expected_ramp_date'] as string)
      ),
    text: (row) => str(row['actual_ramp_date'] ?? row['expected_ramp_date']),
  },
  {
    id: 'days_in_current_stage',
    label: 'Days in stage',
    sortKey: 'days_in_current_stage',
    align: 'right',
    width: 112,
    render: (row) => str(row['days_in_current_stage']) || '—',
    text: (row) => str(row['days_in_current_stage']),
  },
  {
    id: 'next_step',
    label: 'Next step',
    sortKey: 'next_step',
    width: 256,
    render: (row) => (
      <span className="block truncate" title={str(row['next_step'])}>
        {str(row['next_step']) || '—'}
      </span>
    ),
    text: (row) => str(row['next_step']),
  },
  {
    id: 'risk',
    label: 'Risk',
    sortKey: 'risk',
    width: 224,
    render: (row) => <RiskBadges row={row} max={2} />,
    text: (row) =>
      Object.keys(row)
        .filter((key) => key.startsWith('stalled_') && row[key] === true)
        .join(' '),
  },
  {
    id: 'rep_firm',
    label: 'Rep firm',
    sortKey: 'rep_firm',
    width: 176,
    render: (row) => str(row['rep_firm_name']) || '—',
    text: (row) => str(row['rep_firm_name']),
  },
  {
    id: 'business_units',
    label: 'BU mix',
    sortKey: 'business_units',
    width: 208,
    render: (row) => <BusinessUnitChips units={row['business_units']} />,
    text: (row) =>
      Array.isArray(row['business_units']) ? (row['business_units'] as string[]).join(' ') : '',
  },
  {
    id: 'forecast_category',
    label: 'Forecast',
    sortKey: 'forecast_category',
    width: 112,
    render: (row) => humanize(str(row['forecast_category'])),
    text: (row) => str(row['forecast_category']),
  },
  {
    id: 'segment',
    label: 'Segment',
    sortKey: 'segment',
    width: 160,
    render: (row) => humanize(str(row['segment'])),
    text: (row) => str(row['segment']),
  },
  {
    id: 'region',
    label: 'Region',
    sortKey: 'region',
    width: 96,
    render: (row) => str(row['region']),
    text: (row) => str(row['region']),
  },
  {
    // Session 4. Not in the default set; offered in the column chooser.
    id: 'is_focus',
    label: 'Focus',
    sortKey: 'focus',
    width: 80,
    render: (row) =>
      row['is_focus'] === true ? (
        <span title={FOCUS_HELP_TEXT} aria-label="Focus">
          ●
        </span>
      ) : (
        ''
      ),
    text: (row) => (row['is_focus'] === true ? 'yes' : 'no'),
  },
  {
    id: 'verified',
    label: 'Verified',
    sortKey: 'verified',
    width: 160,
    render: (row) =>
      row['verified_at'] ? (
        <span title={`Verified by ${str(row['verified_by_name']) || 'an unknown user'}`}>
          {formatDate(row['verified_at'] as string)}
        </span>
      ) : (
        '—'
      ),
    text: (row) =>
      row['verified_at'] ? `${str(row['verified_at'])} ${str(row['verified_by_name'])}`.trim() : '',
  },
  {
    id: 'expected_decision_date',
    label: 'Decision date',
    sortKey: 'expected_decision_date',
    width: 128,
    render: (row) => formatDate(row['expected_decision_date'] as string),
    text: (row) => str(row['expected_decision_date']),
  },
];

export const DEFAULT_COLUMN_IDS = [
  'opp_number',
  'name',
  'account_name',
  'stage',
  'owner_name',
  'annual_revenue_net',
  'lifetime_value',
  'effective_probability',
  'created_date',
  'expected_ramp_date',
  'days_in_current_stage',
  'next_step',
  'risk',
];

export const COLUMN_STORAGE_KEY = 'menlo-funnel.columns';

/** The shape written today. Version 1 was a bare array of ids. */
export const COLUMN_PREFS_VERSION = 2;

export interface ColumnPrefs {
  ids: string[];
  /** Per-column widths in pixels; a column absent here uses its default. */
  widths: Record<string, number>;
}

function knownIds(candidate: unknown[]): string[] {
  const known = candidate.filter((id): id is string => COLUMNS.some((column) => column.id === id));
  return known.length > 0 ? known : DEFAULT_COLUMN_IDS;
}

function knownWidths(candidate: unknown): Record<string, number> {
  if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) return {};
  const widths: Record<string, number> = {};
  for (const [id, value] of Object.entries(candidate as Record<string, unknown>)) {
    if (!COLUMNS.some((column) => column.id === id)) continue;
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    widths[id] = clampColumnWidth(value);
  }
  return widths;
}

/**
 * **Reads both shapes.** Live users have version 1 — a bare `['opp_number',
 * …]` array — sitting in their browsers right now, and a loader that only
 * understood `{ ids, widths }` would silently reset every one of them to the
 * default column set on their next load. A bare array is therefore still a
 * valid stored value, and simply carries no widths.
 *
 * Every fallback lands on the defaults: a missing key, junk that is neither
 * array nor object, ids that no longer exist in `COLUMNS`, and a `localStorage`
 * that throws — a private window, or a browser set to block storage.
 */
export function loadColumnPrefs(): ColumnPrefs {
  try {
    const raw = window.localStorage.getItem(COLUMN_STORAGE_KEY);
    if (raw === null) return { ids: DEFAULT_COLUMN_IDS, widths: {} };
    const parsed: unknown = JSON.parse(raw);

    // Version 1: the bare array of ids.
    if (Array.isArray(parsed)) return { ids: knownIds(parsed), widths: {} };

    if (typeof parsed === 'object' && parsed !== null) {
      const record = parsed as { ids?: unknown; widths?: unknown };
      if (Array.isArray(record.ids)) {
        return { ids: knownIds(record.ids), widths: knownWidths(record.widths) };
      }
    }

    return { ids: DEFAULT_COLUMN_IDS, widths: {} };
  } catch {
    // A private window, cleared site data, or a browser set to block storage.
    return { ids: DEFAULT_COLUMN_IDS, widths: {} };
  }
}

export function storeColumnPrefs(prefs: ColumnPrefs): void {
  try {
    window.localStorage.setItem(
      COLUMN_STORAGE_KEY,
      JSON.stringify({ v: COLUMN_PREFS_VERSION, ids: prefs.ids, widths: prefs.widths }),
    );
  } catch {
    // Losing a column preference is not worth an error toast.
  }
}
